/**
 * Module dependencies.
 */

import { Carousel, Slide, Type } from 'src/components/core';
import { ReactElement, useEffect, useRef, useState } from 'react';
import { TechnologyProps } from 'src/types/technologies';
import { media, units } from '@untile/react-components';
import { theme } from 'styled-tools';
import { useLocalizedRoute } from 'src/core/utils/routes';
import CarouselSquareCard from 'src/components/cards/carousel-square-card';
import Container from 'src/components/core/layout/container';
import map from 'lodash/map';
import size from 'lodash/size';
import styled from 'styled-components';

/**
 * `Props` type.
 */

type Props = {
  className?: string;
  items: TechnologyProps[];
  title: string;
};

/**
 * `Wrapper` styled component.
 */

const Wrapper = styled.section`
  padding: ${units(10)} 0 ${units(8)};
  position: relative;

  ${media.min('md')`
    padding: ${units(16.5)} 0 ${units(17)};
  `}
`;

/**
 * `StyledContainer` styled component.
 */

const StyledContainer = styled(Container)`
  padding: 0;
`;

/**
 * `Grid` styled component.
 */

const Grid = styled.div`
  display: grid;
  grid-template-areas:
    '.        title    .'
    'carousel carousel carousel';
  grid-template-columns: ${theme('grid.gutterMobile')}px 1fr ${theme(
      'grid.gutterMobile'
    )}px;

  ${media.min('md')`
    grid-template-columns: 1fr 10fr 1fr;
  `}
`;

/**
 * `Title` styled component.
 */

const Title = styled(Type.H2)`
  grid-area: title;
  margin-bottom: ${units(4)};

  ${media.min('md')`
    margin-bottom: ${units(6)};
    text-align: center;
  `}
`;

/**
 * `CarouselWrapper` styled component.
 */

const CarouselWrapper = styled.div`
  grid-area: carousel;
  margin-bottom: ${units(3)};
  position: relative;

  ${media.min('md')`
    margin-bottom: ${units(1)};
  `}
`;

/**
 * Carousel config.
 */

const carouselConfig = {
  breakpoints: {
    576: {
      slidesPerView: 1.35,
      spaceBetween: 20
    },
    768: {
      slidesPerView: 1.8,
      spaceBetween: 30
    },
    1440: {
      slidesPerView: 2.2,
      spaceBetween: 30
    },
    1920: {
      slidesPerView: 2.9,
      spaceBetween: 40
    },
    2560: {
      slidesPerView: 3.85,
      spaceBetween: 40
    },
    3840: {
      slidesPerView: 5.6,
      spaceBetween: 60
    }
  }
};

/**
 * `TechnologiesCarouselSection` component.
 */

const TechnologiesCarouselSection = (props: Props): ReactElement => {
  const { className, items, title } = props;
  const [activeSlide, setActiveSlide] = useState<number>(0);
  const [showCarouselNavigation, setShowCarouselNavigation] =
    useState<boolean>();

  const routeResolver = useLocalizedRoute();
  const sectionRef = useRef<HTMLDivElement>(null);
  const sliderRef = useRef<any>();
  const slideRef = useRef<HTMLDivElement>(null);
  const total = size(items);
  const animProps = {
    'data-scroll': 'fade-in-up',
    'data-scroll-offset': '10%'
  };

  useEffect(() => {
    function handleWindowResize() {
      if (sectionRef && sectionRef?.current && slideRef && slideRef?.current) {
        const cardWidth = slideRef.current.getBoundingClientRect().width;
        const cardMargin = parseFloat(
          slideRef.current?.style?.marginRight ?? '0px'
        );

        const sliderWidth =
          (sliderRef.current.getBoundingClientRect().width * 10) / 12;

        setShowCarouselNavigation(
          total * (cardWidth + cardMargin) > sliderWidth
        );
      }
    }

    handleWindowResize();

    window.addEventListener('resize', handleWindowResize);

    return () => window.removeEventListener('resize', handleWindowResize);
  }, [total]);

  if (total === 0) {
    return null;
  }

  return (
    <Wrapper
      className={className}
      ref={sectionRef}
    >
      <StyledContainer>
        <Grid>
          <Title {...animProps}>{title}</Title>

          <CarouselWrapper
            data-scroll={'fade-in-up-small'}
            data-scroll-offset={'20%'}
          >
            <Carousel
              activeSlide={activeSlide}
              carouselConfig={carouselConfig}
              containerModifierClass={'technologies'}
              onSetActiveSlide={setActiveSlide}
              ref={sliderRef}
              showArrows={showCarouselNavigation}
              showScrollbar={showCarouselNavigation}
              totalItems={total}
            >
              {map(items, (props, index) => (
                <Slide
                  key={index}
                  // @ts-expect-error - `swiper/react` doesn't expect `ref` prop.
                  ref={slideRef}
                >
                  <CarouselSquareCard
                    image={props.listImage}
                    url={routeResolver('technologyDetails', {
                      slug: props.slug
                    })}
                    {...props}
                  />
                </Slide>
              ))}
            </Carousel>
          </CarouselWrapper>
        </Grid>
      </StyledContainer>
    </Wrapper>
  );
};

/**
 * Export `TechnologiesCarouselSection` component.
 */

export default TechnologiesCarouselSection;
